// ============================================================
// useProjects — Square Code portfolio visibility.
//
// Real mode: queries /api/projects/list (server-side, RLS-aware)
// and surfaces the result. Errors are surfaced through `error`,
// not swallowed into a synthetic portfolio.
//
// Demo mode (VITE_OLIVIUM_MODE !== 'live'): returns a small,
// clearly-labeled fallback portfolio so the shell remains
// renderable for marketing screenshots and offline development.
// Every demo row carries `__demo: true` so downstream callers
// can refuse to mutate them.
//
// Live error / missing table: returns an empty list and surfaces
// the error via the hook's `error` field. The Projects index
// renders an honest empty state in that branch — never substitutes
// fake rows when the user expects truth (CLAUDE.md §4).
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { getRuntime } from './_mode'
import type { Project } from './types'
import { api, ApiError } from '../api/client'
import { captureHookFailure, recordHookOutcome } from '../observability/sentry'

interface DemoProject extends Project {
  __demo?: true
}

function seededInt(seed: string, min: number, max: number): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)
  }
  const n = Math.abs(h % 1000) / 1000
  return Math.round(min + (max - min) * n)
}

function generateDemoProjects(): DemoProject[] {
  const names = [
    { name: 'Law Pocket', domain: 'domain' },
    { name: 'Prince Hall', domain: 'domain' },
    { name: 'OLIVIUM', domain: 'core' },
    { name: 'Square Code', domain: 'core' },
    { name: 'Silver Key Realty', domain: 'domain' },
    { name: 'KelleyReno', domain: 'domain' },
  ]
  const base: DemoProject[] = names.map(({ name, domain }, idx) => ({
    id: `demo-project-${idx + 1}`,
    slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    name,
    client_name: name,
    status: idx === 4 ? 'paused' : 'active',
    domain,
    health_score: seededInt(name, 72, 98),
    open_requests: seededInt(`${name}-rq`, 1, 12),
    active_files: seededInt(`${name}-fl`, 4, 39),
    updated_at: new Date(Date.now() - seededInt(`${name}-ts`, 1, 240) * 60_000).toISOString(),
    __demo: true,
  }))

  // Operator-control project — Olivium Commandery. Square Code is the
  // operator/control environment; this is the control-plane project that
  // governs Commandery / Secure Share / Secure Shield readiness. It is NOT
  // a client-facing Olivium launcher item. Seeded as a clearly-flagged demo
  // row (__demo: true) so it is reviewable without any DB write. A live
  // project named "Olivium Commandery" derives the same slug, so the
  // Commandery control surface lights up for either source.
  base.push({
    id: 'demo-project-commandery',
    slug: 'olivium-commandery',
    name: 'Olivium Commandery',
    client_name: 'Operator Control',
    status: 'active',
    domain: 'operator-control',
    health_score: 64,
    open_requests: 3,
    active_files: 7,
    updated_at: new Date(Date.now() - 18 * 60_000).toISOString(),
    __demo: true,
  })

  return base
}

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    const { live } = getRuntime()
    if (!live) {
      // Honest demo branch — flagged rows, never mixed with real data.
      const demo = generateDemoProjects()
      setProjects(demo)
      setLoading(false)
      recordHookOutcome('useProjects', 'demo_fallback', { rows: demo.length })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.projects.list()
      const rows = (result.projects || []).map((p) => ({
        id: p.id,
        slug: p.slug,
        name: p.name,
        client_name: p.client_name ?? undefined,
        status: p.status,
        domain: p.domain ?? undefined,
        health_score: p.health_score ?? undefined,
        // Counters are not in the list payload — populated by useProject(id).
        // Leave undefined so the shell shows "—" rather than fabricated zeros.
        open_requests: undefined,
        active_files: undefined,
        updated_at: p.updated_at,
      })) as Project[]
      setProjects(rows)
      recordHookOutcome('useProjects', 'live_success', {
        route: '/api/projects/list',
        rows: rows.length,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      const message = apiErr?.message || 'projects_list_failed'
      setError(message)
      setProjects([])
      captureHookFailure('useProjects', 'live_error', err, {
        route: '/api/projects/list',
        durationMs: Date.now() - startedAt,
        code: apiErr?.code,
        status: apiErr?.status,
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      await load()
      if (cancelled) return
    })()
    return () => { cancelled = true }
  }, [load])

  return { projects, loading, error, refresh: load }
}

// ------------------------------------------------------------
// useProject(id) — single-project hook with derived counters.
// Calls /api/projects/get which returns { project, counters }.
// In demo mode, locates the row inside generateDemoProjects().
// ------------------------------------------------------------

export interface ProjectCounters {
  open_requests: number | null
  active_files: number | null
  last_run_at: string | null
}

export function useProject(id: string | undefined) {
  const [project, setProject] = useState<Project | null>(null)
  const [counters, setCounters] = useState<ProjectCounters | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!id) {
      setLoading(false)
      setProject(null)
      setCounters(null)
      return
    }
    setLoading(true)
    setError(null)

    const { live } = getRuntime()
    if (!live) {
      const demo = generateDemoProjects().find((p) => p.id === id) || null
      setProject(demo)
      setCounters(
        demo
          ? {
              open_requests: demo.open_requests ?? null,
              active_files: demo.active_files ?? null,
              last_run_at: null,
            }
          : null,
      )
      setLoading(false)
      recordHookOutcome('useProject', 'demo_fallback', { found: !!demo })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.projects.get(id)
      const p = result.project
      setProject({
        id: p.id,
        slug: p.slug,
        name: p.name,
        client_name: p.client_name ?? undefined,
        status: p.status,
        domain: p.domain ?? undefined,
        health_score: p.health_score ?? undefined,
        open_requests: result.counters.open_requests ?? undefined,
        active_files: result.counters.active_files ?? undefined,
        latest_run_status: result.counters.latest_run_status ?? undefined,
        updated_at: p.updated_at,
      })
      setCounters(result.counters)
      recordHookOutcome('useProject', 'live_success', {
        route: '/api/projects/get',
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      setProject(null)
      setCounters(null)
      // 404 is not a hook failure — it's a legitimate "not found".
      if (apiErr?.status === 404) {
        setError('project_not_found')
        recordHookOutcome('useProject', 'missing_dependency', { id, status: 404 })
      } else {
        setError(apiErr?.message || 'project_get_failed')
        captureHookFailure('useProject', 'live_error', err, {
          route: '/api/projects/get',
          id,
          code: apiErr?.code,
          status: apiErr?.status,
        })
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  return { project, counters, loading, error, refresh: load }
}
